import React, { useContext, useEffect, useState } from "react";
import Link from "next/link";
import dynamic from "next/dynamic";

import { FiHome, FiShoppingCart, FiAlignLeft } from "react-icons/fi";

import { IoSearchOutline } from "react-icons/io5";
import { BsFillChatDotsFill } from "react-icons/bs";

import LoginModal from "@component/modal/LoginModal";
import { SidebarContext } from "@context/SidebarContext";
import CategoryDrawer from "@component/drawer/CategoryDrawer";
import { CartContext } from "@context/CartContext";

const MobileFooter = ({ toggleMobileNavSearch }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const { toggleCartDrawer, toggleCategoryDrawer } = useContext(SidebarContext);
  const { totalItems } = useContext(CartContext);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      // Show footer when scrolling up or at the top
      if (currentScrollY < lastScrollY || currentScrollY < 200) {
        setIsVisible(true);
      }
      // Hide footer when scrolling down and not at the top
      else if (currentScrollY > lastScrollY && currentScrollY > 200) {
        setIsVisible(false);
      }

      setLastScrollY(currentScrollY);
    };

    // Reduced throttle time for better responsiveness
    let throttleTimer = null;
    const throttledHandleScroll = () => {
      if (throttleTimer) return;
      throttleTimer = setTimeout(() => {
        handleScroll();
        throttleTimer = null;
      }, 100); // Reduced from 400ms to 100ms
    };

    window.addEventListener('scroll', throttledHandleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', throttledHandleScroll);
      if (throttleTimer) {
        clearTimeout(throttleTimer);
      }
    };
  }, [lastScrollY]);

  return <>
    <LoginModal modalOpen={modalOpen} setModalOpen={setModalOpen} />
    <div className="flex flex-col h-full justify-between align-middle bg-white rounded cursor-pointer overflow-y-scroll flex-grow scrollbar-hide w-full">
      <CategoryDrawer className="w-6 h-6 drop-shadow-xl" />
    </div>

    <footer className={`lg:hidden fixed z-[20] bottom-2 flex justify-between w-full h-16 transition-transform duration-300 ease-in-out ${isVisible ? 'translate-y-0' : 'translate-y-32'
      }`}>
      <div className="inline-flex mx-auto justify-between w-[92%] rounded-2xl shadow-xl shadow-gray-800/20 bg-gradient-to-r from-gray-900 to-black backdrop-blur-md">
        <div className="flex items-center justify-around w-full px-1">
          {/* Category Menu Button */}
          <button
            className="flex flex-col items-center justify-center p-2 px-4 min-w-[50px] transition-colors duration-300 rounded-xl hover:bg-black/40"
            aria-label="Categories"
            onClick={toggleCategoryDrawer}
          >
            <FiAlignLeft className="w-5 h-5 text-white mb-1" />
            <span className="text-[9px] text-gray-300">Menu</span>
          </button>

          {/* Home Button */}
          <Link href="/" className="flex flex-col items-center justify-center p-2 px-4 min-w-[50px] transition-colors duration-300 rounded-xl hover:bg-black/40">
            <FiHome className="w-5 h-5 text-white mb-1" />
            <span className="text-[9px] text-gray-300">Home</span>
          </Link>

          {/* Search Button - Centered with special styling */}
          <button
            onClick={() => {
              toggleMobileNavSearch();
            }
            }
            className="relative flex flex-col items-center justify-center min-w-[50px] py-1"
          >
            <div className="absolute -top-8 p-4 rounded-full border-[3px] border-white bg-gradient-to-br from-red-600 to-red-700 shadow-lg transform transition-transform active:scale-90 hover:shadow-red-500/30 hover:-translate-y-1">
              <IoSearchOutline className="w-6 h-6 text-white" />
            </div>
            {/* Expanded tap area for the search button */}
            <div className="absolute top-[-32px] w-16 h-16 z-[-1]"></div>
            <span className="text-[9px] text-gray-300 mt-6 pt-1">Search</span>
          </button>

          {/* Cart Button */}
          <button
            onClick={toggleCartDrawer}
            className="flex flex-col items-center justify-center p-2 px-4 min-w-[50px] transition-colors duration-300 rounded-xl hover:bg-black/40 relative"
          >
            <div className="relative">
              <FiShoppingCart className="w-5 h-5 text-white mb-1" />
              {totalItems > 0 && (
                <span className="absolute -top-2 -right-2 flex items-center justify-center w-4 h-4 text-[10px] font-bold bg-red-500 text-white rounded-full">
                  {totalItems}
                </span>
              )}
            </div>
            <span className="text-[9px] text-gray-300">Cart</span>
          </button>

          {/* Chat Button */}
          <button
            aria-label="Chat"
            type="button"
            className="flex flex-col items-center justify-center p-2 px-4 min-w-[50px] transition-colors duration-300 rounded-xl hover:bg-black/40"
            onClick={() => {
              if (window.$chatwoot) {
                window.$chatwoot.toggle();
              }
            }}
          >
            <BsFillChatDotsFill className="w-5 h-5 text-white mb-1" />
            <span className="text-[9px] text-gray-300">Chat</span>
          </button>
        </div>
      </div>
    </footer>
  </>;
};

export default dynamic(() => Promise.resolve(MobileFooter), { ssr: false });
